/**
 * 채팅 — 폰 Room 브릿지 기반(서버 저장 X). 로컬 검색 컨텍스트 → /api/proxy/chat 답변.
 * 메시지는 room.chat.observeMessages 구독으로 실시간(사용자 즉시 + assistant pending→완료 push).
 */
import { useEffect, useRef, useState } from 'react';
import { room } from '../lib/roomBridge';
import { ChatBubble, TypingBubble } from '../components/ChatBubble';

interface ChatMsg {
  id: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  status: 'pending' | 'done' | 'failed';
  createdAt: number;
}

export function Chat() {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  // 활성 스레드 메시지 구독 (assistant가 준비되면 자동 push).
  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      return;
    }
    const unsub = room.chat.observeMessages(threadId, (rows) => setMessages(rows as ChatMsg[]));
    return unsub;
  }, [threadId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    setSending(true);
    setError(null);
    try {
      const res = await room.chat.send(text, threadId ?? undefined);
      if (!threadId && res?.threadId) setThreadId(res.threadId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setDraft(text);
    } finally {
      setSending(false);
    }
  }

  const visible = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
  const showEmpty = visible.length === 0 && !sending;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)]">
      <header className="px-xl pt-2xl pb-md border-b border-[var(--border)] flex items-center gap-sm">
        <div className="w-[28px] h-[28px] rounded-md bg-[var(--accent)] text-[var(--accent-on)] grid place-items-center font-display font-bold text-[12px]">
          B
        </div>
        <div className="flex-1">
          <div className="font-display text-[18px] font-medium text-[var(--text-primary)] leading-tight">Butler</div>
          <div className="text-[11px] text-[var(--text-muted)] tracking-wider uppercase">통화·문자 기록 기반 답변</div>
        </div>
        {threadId && (
          <button
            onClick={() => setThreadId(null)}
            className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
          >
            새 채팅
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-xl py-md space-y-md pb-[100px]">
        {showEmpty && (
          <div className="text-center text-[13px] text-[var(--text-muted)] py-2xl">
            무엇이 궁금하세요?
            <br />
            <span className="text-[12px]">예: "지난주 김 차장 통화 뭐였지?" "이번 주 미해결 할 일 보여줘"</span>
          </div>
        )}
        {visible.map((m) =>
          m.role === 'assistant' && m.status === 'pending' ? (
            <TypingBubble key={m.id} label="Butler" />
          ) : (
            <ChatBubble
              key={m.id}
              role={m.role}
              text={m.content}
              label={m.role === 'assistant' ? 'Butler' : undefined}
            />
          ),
        )}
        <div ref={endRef} />
      </main>

      <footer className="sticky bottom-[64px] left-0 right-0 px-xl py-sm bg-[var(--bg)] border-t border-[var(--border)]">
        {error && (
          <div className="mb-sm text-[11px] text-[var(--error)] bg-[var(--surface-elev)] rounded-md p-sm">{error}</div>
        )}
        <form onSubmit={onSubmit} className="flex gap-sm items-center">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Butler에게 물어보세요…"
            className="flex-1 px-md py-[10px] rounded-full bg-[var(--surface-elev)] border border-[var(--border)] text-[var(--text-primary)] text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="w-[38px] h-[38px] rounded-full bg-[var(--accent)] hover:bg-[var(--accent-press)] text-[var(--accent-on)] disabled:opacity-50 grid place-items-center text-[14px]"
            aria-label="보내기"
          >
            ↑
          </button>
        </form>
      </footer>
    </div>
  );
}
